import { AsyncPipe, DatePipe, NgClass, NgFor, NgIf, NgTemplateOutlet } from '@angular/common';
import { Component, OnInit, ViewChild } from '@angular/core';
import { FormsModule, NgModel, ReactiveFormsModule } from '@angular/forms';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { ActivatedRoute, Params, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { I18nService } from '@app/i18n';
import { Store } from '@ngrx/store';
import { FormlyModule } from '@ngx-formly/core';
import {
  AuthActions,
  FeedbackComponent,
  ModalComponent,
  NavigationComponent,
  PaginationComponent,
  ROUTE_HOME,
  UntilDestroy,
  selectAuthInfo,
  selectIsInstructor,
  untilDestroyed,
} from '@shared';
import { AuthenticationService } from '@shared/services/auth/authentication.service';
import { CartStateV2, selectCartV2 } from '@shared/store/cartV2';
import { AngularSvgIconModule } from 'angular-svg-icon';
import * as _ from 'lodash';
import { DropdownItemDirective } from 'projects/thkee-common/src/lib/directives/dropdown/dropdown-item.directive';
import { DropdownMenuDirective } from 'projects/thkee-common/src/lib/directives/dropdown/dropdown-menu.directive';
import { DropdownToggleDirective } from 'projects/thkee-common/src/lib/directives/dropdown/dropdown-toggle.directive';
import { DropdownDirective } from 'projects/thkee-common/src/lib/directives/dropdown/dropdown.directive';
import { Observable, distinctUntilChanged, filter, firstValueFrom } from 'rxjs';
import {
  AvatarComponent,
  CategoryV2,
  LoaderService,
  ProcessingDirective,
  RouterStoreService,
  ToastService,
  User,
  selectCategories,
  skipUndefined,
} from 'thkee-common';

@UntilDestroy()
@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  standalone: true,
  imports: [
    NgIf,
    AngularSvgIconModule,
    NgClass,
    FeedbackComponent,
    ModalComponent,
    AsyncPipe,
    ProcessingDirective,
    ReactiveFormsModule,
    FormsModule,
    FormlyModule,
    NgTemplateOutlet,
    NavigationComponent,
    PaginationComponent,
    RouterLink,
    DatePipe,
    NgFor,
    AvatarComponent,
    RouterLinkActive,
    DropdownDirective,
    DropdownToggleDirective,
    DropdownMenuDirective,
    DropdownItemDirective,
  ],
})
export class HeaderComponent implements OnInit {
  @ViewChild('mobileNav') mobileNav!: ModalComponent;
  @ViewChild('searchRef') searchRef!: NgModel;
  selectedCtg: string = '';
  ctgLabel: string = $localize`All categories`;

  homeUrl: string = ROUTE_HOME;
  openNavigation: boolean = false;
  openAccount: boolean = false;

  isAuth$ = this.authService.sessionExist();
  isInstructor$ = this.store.select(selectIsInstructor).pipe(skipUndefined());
  categories$ = this.store.select(selectCategories);
  userAuthInfo$ = this.store.select(selectAuthInfo);

  showSearchBar: boolean = false;
  searchKeyword: string = '';

  cart$!: Observable<CartStateV2>;
  cartCount: number = 0;

  user?: User;
  isPreviewCourse: boolean = false;
  params: Params = {};
  queryParams: Params = {};

  isLoading$ = this.loaderService.response('loading-me');

  constructor(
    private router: Router,
    private store: Store,
    private readonly toastService: ToastService,
    private activatedRoute: ActivatedRoute,
    private routerStore: RouterStoreService,
    private loaderService: LoaderService,
    private i18nService: I18nService,
    private sanitizer: DomSanitizer,
    private authService: AuthenticationService
  ) {}

  ngOnInit() {
    this.activatedRoute.queryParams.pipe(untilDestroyed(this)).subscribe((params) => {
      this.queryParams = params;
      if (params['preview'] === 'true') {
        this.isPreviewCourse = true;
      } else {
        this.isPreviewCourse = false;
      }
    });

    this.routerStore
      .getParams()
      .pipe(distinctUntilChanged(), untilDestroyed(this))
      .subscribe((params) => {
        this.params = params;
      });

    this.cart$ = this.store.select(selectCartV2);
    this.cart$.pipe(untilDestroyed(this)).subscribe((cart) => {
      if (cart?.courses) {
        this.cartCount = cart.courses.length;
      } else {
        this.cartCount = 0;
      }
    });

    firstValueFrom(this.authService.sessionExist()).then((exists) => {
      if (exists) {
        this.store
          .select(selectAuthInfo)
          .pipe(filter(Boolean), untilDestroyed(this))
          .subscribe((user) => {
            if (user.cart_item_count && user.cart_item_count > 0) {
              this.cartCount = user.cart_item_count;
            } else {
              this.cartCount = 0;
            }
            return (this.user = {
              id: user.id,
              email: user.email,
              fullname: user.fullname,
              photo_url: user.photo_url,
            } as User);
          });
      }
    });
  }

  sanitizeUrl(url?: string): SafeUrl {
    return this.sanitizer.bypassSecurityTrustUrl(url ?? 'assets/images/static/fallback.png');
  }

  logout() {
    this.store.dispatch(AuthActions.logout());
  }

  handleClickOutside() {
    this.openNavigation = false;
  }

  mostPopular(categories: CategoryV2[]): CategoryV2[] {
    return _.slice(categories, 2, 5); // TODO: Get/Fetch the real most popular
  }

  filterCategory(categories: any) {
    if (!this.selectedCtg || this.selectedCtg == 'all') {
      // this.ctgLabel = 'All categories';
      return categories;
    }
    const parentCtg = _.find(categories, { id: this.selectedCtg });
    if (parentCtg) {
      // this.ctgLabel = parentCtg.title;
      return parentCtg.subcategories;
    }

    return categories;
  }

  onSearch(event: any) {
    const trimmedSearchTerm = this.searchKeyword.trim();
    if (trimmedSearchTerm) {
      this.router.navigate(['/search'], { queryParams: { search: trimmedSearchTerm } });
      this.searchKeyword = '';
    } else {
      this.toastService.message({
        message: $localize`The search field will not be empty!`,
      });
    }
  }

  // Temporary header info
  showTempHeader: boolean = false;
  currentLang = this.i18nService.langName;
  supportLanguages = this.i18nService.supportedLanguageObjects;
  changeLanguage(language: string) {
    this.i18nService.language = language;
    this.isDropdown[1] = false;
  }

  isDropdown: boolean[] = [];
  showDropdown(index: number) {
    this.isDropdown[index] = !this.isDropdown[index];
  }

  handleClickOutsideTemp(index: number) {
    this.isDropdown[index] = false;
  }

  goToDashboard() {
    this.router.navigate(['/instructor/courses']);
  }

  becomeInstructor() {
    this.router.navigate(['/become-an-instructor']);
  }

  goToTechSection() {
    this.router.navigate(['/become-an-instructor']);
  }

  login() {
    this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
      this.router.navigate(['/auth'], { queryParams: { show: 'signin' } });
    });
    if (this.mobileNav.isOpened) {
      this.mobileNav.close();
    }
  }

  signup() {
    this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
      this.router.navigate(['/auth'], { queryParams: { show: 'signup' } });
    });
    if (this.mobileNav.isOpened) {
      this.mobileNav.close();
    }
  }
}
